import { VenueDetailsBySlugResponse, useI18N } from "common/hooks";
import { useState, useMemo } from "react";
import _ from "lodash";
import NoteCard from "./NoteCard";
import AnalyticsService from "@/services/analytics";
import { CaretRight, CaretDown, FolderDotted } from "phosphor-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shadcn/components/ui/dropdown-menu";

type ToDoTaskContentProps = {
  venueDetailsResponse: VenueDetailsBySlugResponse | undefined;
  searchTerm: string;
  onRefresh?: () => void;
};

function ToDoTaskContent({
  venueDetailsResponse,
  searchTerm,
  onRefresh,
}: ToDoTaskContentProps) {
  const i18n = useI18N();
  const [selectedTask, setSelectedTask] = useState<string>("");
  const [sortBy, setSortBy] = useState<"newest" | "oldest" | "alphabetical">(
    "newest",
  );
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // Get all business notes tasks
  const businessNotesTasks = useMemo(() => {
    if (!venueDetailsResponse?.businessNotesTasks) {
      return [];
    }
    const tasks = venueDetailsResponse.businessNotesTasks;
    return [...tasks.General, ...tasks.Custom];
  }, [venueDetailsResponse]);

  // Get task-based notes (type ==="nonfloor" && showTasks === 1)
  const taskNotes = useMemo(() => {
    if (
      !venueDetailsResponse?.businessNotesForMappers ||
      !venueDetailsResponse?.enterpriseBusinessNotesActions
    ) {
      return [];
    }

    // Find task-based action IDs (nonfloor with showTasks === 1)
    const taskActionIds: number[] = [];
    venueDetailsResponse.enterpriseBusinessNotesActions.forEach((action) => {
      if (action.children_business_notes_action) {
        action.children_business_notes_action.forEach((childAction) => {
          if (childAction.show_tasks === 1) {
            taskActionIds.push(childAction.id);
          }
        });
      }
    });

    // Filter notes by task action IDs
    return venueDetailsResponse.businessNotesForMappers.filter((note) =>
      taskActionIds.includes(note.business_notes_action_id),
    );
  }, [venueDetailsResponse]);

  // Filter notes based on search term
  const filteredNotes = useMemo(() => {
    if (!searchTerm) {
      return taskNotes;
    }

    // Split search term into words and remove empty strings
    const searchWords = searchTerm
      .toLowerCase()
      .split("")
      .filter((word) => word.trim() !== "");

    // Filter notes where all search words are present in notes, location names, or category hierarchy
    return taskNotes.filter((note) => {
      const noteText = note.notes.toLowerCase();

      // Collect all location-related text
      const locationTexts = note.locationAreasData
        .map((loc) => {
          const parts = [
            loc.locationName || "",
            loc.categoryHierarchy || "",
            loc.locationType || "",
          ];
          return parts.join("").toLowerCase();
        })
        .join("");

      const combinedText = `${noteText} ${locationTexts}`;
      return searchWords.every((word) => combinedText.includes(word));
    });
  }, [taskNotes, searchTerm]);

  // Group notes by task
  const groupedNotes = useMemo(() => {
    // Group by task ID
    const groupedByTask = _.groupBy(filteredNotes, "business_notes_task_id");

    return Object.entries(groupedByTask).map(([taskId, notes]) => {
      const typedNotes = notes as typeof filteredNotes;

      // Apply sorting to notes within each group
      const sortedNotes = [...typedNotes].sort((a, b) => {
        if (sortBy === "newest") {
          // Sort by date added, newest first
          return (
            new Date(b.business_notes_created_at).getTime() -
            new Date(a.business_notes_created_at).getTime()
          );
        } else if (sortBy === "oldest") {
          // Sort by date added, oldest first
          return (
            new Date(a.business_notes_created_at).getTime() -
            new Date(b.business_notes_created_at).getTime()
          );
        } else {
          // Sort alphabetically by note text
          return a.notes.localeCompare(b.notes);
        }
      });

      return {
        notes: sortedNotes,
        taskDetails: businessNotesTasks.find(
          (task) => task.id === Number(taskId),
        ),
      };
    });
  }, [filteredNotes, businessNotesTasks, sortBy]);

  // Get unique tasks for filter tabs (no"All" option)
  const taskFilters = useMemo(() => {
    return groupedNotes
      .map((group) => {
        const title = group.taskDetails?.task || "General Task";
        return {
          id: group.taskDetails?.id?.toString() || "general",
          title,
          count: group.notes.length,
        };
      })
      .filter((filter) => filter.count > 0);
  }, [groupedNotes]);

  // Always display all groups (no filtering)
  const displayedGroups = groupedNotes;

  // Get total notes count for display
  const displayedNotesCount = useMemo(() => {
    return filteredNotes.length;
  }, [filteredNotes]);

  // Set initial task selection to first item
  useMemo(() => {
    if (taskFilters.length > 0 && !selectedTask) {
      setSelectedTask(taskFilters[0].id);
    }
  }, [taskFilters, selectedTask]);

  // Scroll to selected section
  const scrollToTask = (taskId: string) => {
    setSelectedTask(taskId);
    // Analytics tracking
    AnalyticsService.trackGAEventHandler("alertsTabTaskFilterClicked");
    AnalyticsService.trackMx("alertsTabTaskFilterClicked", { TaskId: taskId });
    // Hide mobile menu when filter is selected
    setShowMobileMenu(false);

    // Add a small delay to ensure the state is updated
    setTimeout(() => {
      const sectionId = `task-section-${taskId}`;
      const element = document.getElementById(sectionId);
      if (element) {
        // Find the scrollable container
        const container = element.closest(".overflow-y-auto");

        if (container) {
          // Get accurate positioning using getBoundingClientRect
          const containerRect = container.getBoundingClientRect();
          const elementRect = element.getBoundingClientRect();

          // Calculate the element's position relative to the scrollable container
          const relativeTop =
            elementRect.top - containerRect.top + container.scrollTop;

          // Scroll to position the section title near the top with proper padding
          const scrollTop = relativeTop - 32;

          container.scrollTo({
            top: Math.max(0, scrollTop),
            behavior: "smooth",
          });
        } else {
          // Fallback: use browser's scrollIntoView
          element.scrollIntoView({
            behavior: "smooth",
            block: "start",
            inline: "nearest",
          });
        }
      }
    }, 50);
  };

  return (
    <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
      {displayedGroups.length > 0 && (
        <>
      {/* Sidebar Navigation */}
      <aside
        className={`w-full md:w-64 lg:w-72 bg-bg-brand-secondary-default border-b md:border-b-0 md:border-r border-border-token-neutral-minimal overflow-y-auto flex-shrink-0 ${
          showMobileMenu ? "flex py-6 max-h-[50vh]" : "hidden md:flex md:py-6"
        }`}
        aria-label={i18n.get("taskFilter")}
      >
        <nav>
          <ul>
            {taskFilters.map((filter) => (
              <li key={filter.id} className="relative">
                <button
                  type="button"
                  onClick={() => scrollToTask(filter.id)}
                  aria-current={selectedTask === filter.id ? 'true' : undefined}
                  className={`w-full flex items-center gap-3 pl-[52px] pr-4 py-3 cursor-pointer relative group focus-blue ${
                    selectedTask === filter.id
                      ? "bg-container-brand-minimal"
                      : "bg-bg-brand-secondary-default"
                  }`}
                >
                  {/* Vertical bar indicator */}
                  <div
                    className={`absolute left-[27px] top-0 bottom-0 w-[2px] transition-colors ${
                      selectedTask === filter.id
                        ? "bg-[#352FC3]"
                        : "bg-[#DEDDF7] group-hover:bg-[#352FC3]"
                    }`}
                  ></div>

                  {/* Task title and count */}
                  <span
                    className={`flex-1 font-medium label-m-medium text-left ${
                      selectedTask === filter.id
                        ? "text-fg-brand-subtle"
                        : "text-fg-neutral-strong"
                    }`}
                  >
                    {filter.title} 
                    {/* ({filter.count}) */}
                  </span>

                  {/* Caret icon */}
                  <CaretRight size={16} className="text-gray-600" />
                </button>
              </li>
            ))}
          </ul>
        </nav>
      </aside>
      </>
      )}
      
      {/* Main Content */}
      <main className="flex-1 px-4 pt-6 pb-64 min-h-0 overflow-y-auto flex flex-col">
        {/* Mobile Menu Button - Only show when sidebar is collapsed */}
        {!showMobileMenu && displayedNotesCount > 0 && (
          <div className="md:hidden mb-4">
            <button
              onClick={() => setShowMobileMenu(true)}
              className="flex items-center gap-2 text-fg-brand-subtle font-medium"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
              {i18n.get("menu")}
            </button>
          </div>
        )}

        {/* Top Action Bar */}
        <div className="flex items-center justify-between mb-6">
          <span className="label-m-medium text-fg-neutral-subtle">
            {displayedNotesCount}{" "}
            {displayedNotesCount === 1 ? i18n.get("result") : i18n.get("results")}
          </span>

          {/* Sort Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger className="focus:outline-none focus-visible:outline-none">
              <div className="flex items-center gap-2 px-3 py-2 label-m-medium text-fg-neutral-subtle hover:bg-container-neutral-minimal rounded-md cursor-pointer focus-blue">
                <span className="underline">
                  {sortBy === "newest" && i18n.get("dateAddedNewest")}
                  {sortBy === "oldest" && i18n.get("dateAddedOldest")}
                  {sortBy === "alphabetical" && i18n.get("alphabetical")}
                </span>
                <CaretDown size={16} />
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel aria-label="Sort by">
                {i18n.get("sortBy")}:
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuRadioGroup
                value={sortBy}
                onValueChange={(value) => {
                  setSortBy(value as "newest" | "oldest" | "alphabetical");
                  AnalyticsService.trackGAEventHandler("alertsTabSortChanged");
                  AnalyticsService.trackMx("alertsTabSortChanged", { SortBy: value });
                }}
              >
                <DropdownMenuRadioItem value="newest">
                  {i18n.get("dateAddedNewest")}
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="oldest">
                  {i18n.get("dateAddedOldest")}
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="alphabetical">
                  {i18n.get("alphabetical")}
                </DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Grouped Notes */}
        {displayedGroups.length > 0 ? (
          <div className="space-y-8">
            {displayedGroups.map((group, index) => {
              const title = group.taskDetails?.task || "General Task";
              const sectionId = `task-section-${group.taskDetails?.id?.toString() || "general"}`;

              return (
                <section
                  key={`task-group-${index}`}
                  id={sectionId}
                  className="space-y-4"
                >
                  {/* Section Header */}
                  <h3 className="title-s-semibold text-fg-neutral-strong">
                    {title || i18n.get("generalTask")} ({group.notes.length})
                  </h3>

                  {/* Notes List */}
                  <div className="space-y-4">
                    {group.notes.map((note) => (
                      <NoteCard
                        key={note.business_notes_id}
                        note={note}
                        showLocationInfo={true}
                        businessId={venueDetailsResponse?.details?.business?.id?.toString()}
                        onRefresh={onRefresh}
                        venueName={venueDetailsResponse?.details?.name}
                        noteType="To-Do Task"
                      />
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        ) : (
            <>
              {/* No todo tasks found Content */}
              <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
                <div className="flex-1 px-4 pt-6 pb-64 min-h-0 overflow-y-auto flex flex-col">
                  <div className="text-center py-12" role="status" aria-live="polite">
                    <div className="flex flex-1 flex-col justify-center items-center px-4">
                      <FolderDotted size={32} className="text-fg-neutral-strong" aria-hidden="true" />
                      <p className="text-center title-s-semibold text-fg-neutral-strong mt-3">
                        {i18n.get("alerts")}
                      </p>
                      <p className="text-center label-s-regular text-fg-neutral-subtle mt-2 max-w-sm">
                        {searchTerm
                          ? i18n.get("noNotesFoundMatchingSearch")
                          : i18n.get("noTodoTasksAvailable")}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </> 
        )}
      </main>
    </div>
  );
}

export default ToDoTaskContent;
